import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import { FaDownload, FaEye, FaTimes } from 'react-icons/fa';
import * as API from "../Endpoints/Endpoints";

function ViewChatbot() {
  const [bot, setChatbots] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // ref for modal content so we can reset its scroll
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchbot = async () => {
      try {
        const response = await axios.get(API.GET_RESEARCH_API); // /researchbot
        setChatbots(response.data);
      } catch (error) {
        console.error('Error fetching chatbots:', error);
        toast.error('Failed to fetch chatbots');
      }
    };
    fetchbot();
  }, []);

  // Reset modal scroll to top whenever modal opens
  useEffect(() => {
    if (viewOpen && modalRef.current) {
      // small timeout to ensure DOM painted (usually not needed but safe)
      setTimeout(() => {
        try {
          modalRef.current.scrollTop = 0;
        } catch (e) {
          // ignore
        }
      }, 0);
    }
  }, [viewOpen]);

  // Filter the bots based on the search query
  const filteredBots = bot.filter((item) => {
    const q = searchQuery.toLowerCase();
    return (
      item.useremail?.toLowerCase().includes(q) ||
      item.organizationname?.toLowerCase().includes(q) ||
      item.resdescription?.toLowerCase().includes(q) ||
      item.industry?.toLowerCase().includes(q) ||
      item.themes?.toLowerCase().includes(q) ||
      item.researchtitle?.toLowerCase().includes(q) ||
      item.objectives?.toLowerCase().includes(q) ||
      item.introduction?.toLowerCase().includes(q) ||
      item.abstraction?.toLowerCase().includes(q) ||
      item.bibliography?.toLowerCase().includes(q) ||
      item.methodology?.toLowerCase().includes(q) ||
      item.hypothesis?.toLowerCase().includes(q) ||
      item.likertscale?.toLowerCase().includes(q) ||
      item.dataset?.toLowerCase().includes(q) ||
      item.stattesting?.toLowerCase().includes(q) ||
      item.inferences?.toLowerCase().includes(q) ||
      item.conclusion?.toLowerCase().includes(q)
    );
  });

  // VIEW: fetch from API and open modal with that specific record (by researchid)
  const handleView = async (item) => {
    try {
      setViewLoading(true);
      setViewOpen(true);
      setSelected(null);

      // Fetch the list and pick the specific record by researchid
      const res = await axios.get(API.GET_RESEARCH_API);
      const rec = res.data.find((r) => r.researchid === item.researchid);
      if (!rec) {
        toast.error('Record not found');
        setViewOpen(false);
        return;
      }
      setSelected(rec);
      // modalRef effect will reset scroll when viewOpen becomes true
    } catch (err) {
      console.error(err);
      toast.error('Failed to load details');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  // Keep your existing table-level Download (handleImport) intact
  const handleImport = (item) => {
    const doc = new jsPDF();
    let yPosition = 20;
    const lineHeight = 10;
    const maxWidth = 180;
    const pageHeight = doc.internal.pageSize.height;

    const addText = (text, x, y) => {
      const safe = String(text ?? '');
      const splitText = doc.splitTextToSize(safe, maxWidth);
      doc.text(splitText, x, y);
      return splitText.length * lineHeight;
    };
    const checkAndAddPage = (currentYPosition) => {
      if (currentYPosition + lineHeight > pageHeight) {
        doc.addPage();
        return 20;
      }
      return currentYPosition;
    };
    const addHeading = (heading, x, y) => {
      doc.setFont('helvetica', 'bold');
      y += addText(heading, x, y);
      doc.setFont('helvetica', 'normal');
      return y;
    };

    yPosition = addHeading(`Organization name:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.organizationname, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`User name:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.useremail, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Research Title:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.researchtitle, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Description:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.resdescription, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Industry:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.industry, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Themes:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.themes, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Objectives:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.objectives, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Introduction:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.introduction, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Abstraction:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.abstraction, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Bibliography:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.bibliography, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Methodology:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.methodology, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Hypothesis:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.hypothesis, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Likert Scale Questionnaire:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.likertscale, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Sample Dataset:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.dataset, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Statistical Testing:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.stattesting, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Inferences:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.inferences, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Conclusion:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.conclusion, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    doc.save(`${item.researchtitle}.pdf`);
  };

  return (
    <div className="container mx-auto px-4 mt-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Research References</h1>
        <input
          type="text"
          placeholder="Search..."
          className="px-4 py-2 border rounded-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto">
        {/* table-fixed + colgroup for consistent column sizing */}
        <table className="min-w-full bg-white border-gray-300 shadow-md rounded-md table-fixed">
          <colgroup>
            <col style={{ width: '2%' }} />   {/* Actions */}
            <col style={{ width: '1%' }} />   {/* Sr No */}
            <col style={{ width: '8%' }} />  {/* Research Title (reduced) */}
          </colgroup>

          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Sr No</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Research Title</th>
            </tr>
          </thead>

          <tbody>
            {filteredBots.map((item, index) => (
              <tr key={item.researchid || item.id} className="border-b align-top">
                <td className="px-4 py-3 whitespace-normal">
                  <div className="flex items-center gap-2">
                    <button
                      className="text-indigo-600 hover:underline flex items-center gap-1"
                      onClick={() => handleView(item)}
                      title="View"
                      >
                      <FaEye />
                    </button>
                    <button
                      className="text-blue-500 hover:underline flex items-center gap-1"
                      onClick={() => handleImport(item)}
                      title="Export as PDF"
                    >
                      <FaDownload />
                    </button>
                  </div>
                </td>

                <td className="px-4 py-3 whitespace-normal text-sm">{index + 1}</td>

                <td className="px-4 py-3 whitespace-normal text-sm break-words max-w-[220px]">
                  {item.researchtitle}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL: PDF-style only with Back button */}
      {viewOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-4">
          <div
            ref={modalRef}
            className="bg-white w-full max-w-5xl rounded-2xl shadow-xl p-6 relative overflow-y-auto max-h-[90vh]"
          >
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-black"
              onClick={() => { setViewOpen(false); setSelected(null); }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {viewLoading ? (
              <div className="py-20 text-center">Loading...</div>
            ) : selected ? (
              <>
                {/* PDF-like view */}
                <div id="pdf-like" className="border rounded-xl p-6 bg-white">
                  <div className="mb-1 text-2xl font-semibold break-words">{selected.researchtitle || 'Untitled Research'}</div>
                  <div className="text-sm text-gray-600 mb-4">
                    {selected.useremail ? `By ${selected.useremail}` : ''} {selected.organizationname ? `• ${selected.organizationname}` : ''}
                  </div>

                  <PdfSection title="Research Description" text={selected.resdescription} />
                  <PdfSection title="Industry" text={selected.industry} />
                  <PdfSection title="Themes" text={selected.themes} />
                  <PdfSection title="Objectives" text={selected.objectives} />
                  <PdfSection title="Introduction" text={selected.introduction} />
                  <PdfSection title="Abstraction" text={selected.abstraction} />
                  <PdfSection title="Methodology" text={selected.methodology} />
                  <PdfSection title="Hypothesis" text={selected.hypothesis} />
                  <PdfSection title="Likert Scale Questionnaire" text={selected.likertscale} />
                  <PdfSection title="Sample Dataset" text={selected.dataset} />
                  <PdfSection title="Statistical Testing" text={selected.stattesting} />
                  <PdfSection title="Inferences" text={selected.inferences} />
                  <PdfSection title="Conclusion" text={selected.conclusion} />
                  <PdfSection title="Bibliography" text={selected.bibliography} />
                </div>

                {/* Back button only */}
                <div className="mt-6 flex justify-end">
                  <button
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700"
                    onClick={() => { setViewOpen(false); setSelected(null); }}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <div className="py-20 text-center text-red-600">No record found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Section for the PDF-like view
function PdfSection({ title, text }) {
  if (!text) return null;
  return (
    <div className="mt-5">
      <h3 className="text-lg font-semibold border-b border-gray-200 pb-1">{title}</h3>
      <div className="mt-2 whitespace-pre-wrap leading-relaxed text-gray-800 break-words">
        {text}
      </div>
    </div>
  );
}

export default ViewChatbot;
